import React, {
  Children,
  useRef,
  useState,
  ReactNode,
  RefObject,
  useCallback,
  Fragment,
} from 'react';
import classnames from 'classnames';

import { Icon } from '@BICE_Vida/components';
import styles from './Carousel.module.scss';

const MIN_SWIPE = 100;

type CarouselT = {
  id?: string;
  children: ReactNode;
  className?: string;
  bulletSlide?: boolean;
  showButtons?: boolean;
  gap?: number;
};

type TouchEventType = TouchEvent | React.TouchEvent<HTMLDivElement>;

type SwipeToType = {
  diff: number;
  offsetX: number;
  containerWidth: number;
  minOffsetX: number;
};

const getRefValue = <C,>(ref: RefObject<C>) => {
  return ref.current as C;
};

const getEventData = (e: TouchEventType) => {
  return 'changedTouches' in e ? e.changedTouches[0] : e;
};

const swipeTo = (swipeToProps: SwipeToType) => {
  const { diff, offsetX, containerWidth, minOffsetX } = swipeToProps;
  const outOfMinOffset = offsetX <= minOffsetX;

  if (Math.abs(diff) > MIN_SWIPE && !outOfMinOffset) {
    if (diff > 0) {
      return Math.floor(offsetX / containerWidth) * containerWidth; // to right
    }

    return Math.ceil(offsetX / containerWidth) * containerWidth; // to left
  }

  return Math.round(offsetX / containerWidth) * containerWidth;
};

const getNumberOfSlides = (children: ReactNode) => {
  let elements = 0;

  Children.forEach(children, (child) => {
    if (child instanceof Array) {
      elements += child.length;
    } else {
      elements += 1;
    }
  });

  return elements;
};

function Carousel({
  id,
  children,
  className,
  bulletSlide = false,
  showButtons,
  gap = 0,
}: CarouselT) {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidthRef = useRef(0);

  const minOffsetXRef = useRef(0);
  const currentOffsetXRef = useRef(0);
  const offsetXRef = useRef(0);
  const startXRef = useRef(0);

  const [isSwiping, setIsSwiping] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [spaceBetweenSlides, setSpaceBetweenSlides] = useState(0);

  const numberOfSlides = getNumberOfSlides(children);

  const carouselClass = classnames(styles.carousel, {
    [`${className}`]: className,
  });

  const carouselContainerClass = classnames(styles.carousel_container, {
    [styles.swiping]: isSwiping,
  });

  const bulletClasses = (bulletKey: number) =>
    classnames(styles.carousel_bullet, {
      [styles.carousel_bullet_active]: activeSlide === bulletKey,
    });

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      const currentX = getEventData(e).clientX;
      const diff = getRefValue(startXRef) - currentX;

      let newOffsetX = getRefValue(currentOffsetXRef) - diff;

      const maxOffsetX = 0;
      const minOffsetX = getRefValue(minOffsetXRef);

      if (newOffsetX > maxOffsetX) {
        newOffsetX = 0;
      }

      if (newOffsetX < minOffsetX) {
        newOffsetX = minOffsetX;
      }

      offsetXRef.current = newOffsetX;

      setOffsetX(newOffsetX);
    },
    [getEventData, getRefValue, currentOffsetXRef]
  );

  const onTouchEnd = useCallback(() => {
    const containerWidth = getRefValue(containerWidthRef);
    const currentOffsetX = getRefValue(currentOffsetXRef);

    const offsetX = getRefValue(offsetXRef);
    const minOffsetX = getRefValue(minOffsetXRef);

    const diff = currentOffsetX - offsetX;
    const newOffsetX = swipeTo({ diff, offsetX, containerWidth, minOffsetX });

    offsetXRef.current = newOffsetX;

    setIsSwiping(false);
    setOffsetX(newOffsetX);
    setActiveSlide(Math.abs(newOffsetX / containerWidth));

    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  }, [
    getRefValue,
    containerWidthRef,
    currentOffsetXRef,
    setIsSwiping,
    setOffsetX,
    setActiveSlide,
    swipeTo,
  ]);

  const onTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      setIsSwiping(true);

      currentOffsetXRef.current = getRefValue(offsetXRef);
      startXRef.current = getEventData(e).clientX;

      const containerElement = getRefValue(containerRef);
      const containerWidth = containerElement.offsetWidth;

      const newSpaceBetweenSlides = gap
        ? -gap
        : (containerElement.scrollWidth - containerWidth * numberOfSlides) / (numberOfSlides - 1);

      if (newSpaceBetweenSlides !== spaceBetweenSlides) {
        setSpaceBetweenSlides(newSpaceBetweenSlides);
      }

      minOffsetXRef.current = containerWidth - containerElement.scrollWidth + spaceBetweenSlides;
      containerWidthRef.current = containerWidth;

      window.addEventListener('touchmove', onTouchMove);
      window.addEventListener('touchend', onTouchEnd);
    },
    [setIsSwiping, getRefValue, getEventData, containerRef, setSpaceBetweenSlides, gap]
  );

  const handlerActiveSlide = useCallback(
    (index: number) => {
      const containerElement = getRefValue(containerRef);
      const containerWidth = containerElement.offsetWidth - (gap || 0);

      const offsetX = -(containerWidth * index);

      offsetXRef.current = offsetX;

      setActiveSlide(index);
      setOffsetX(offsetX);
    },
    [containerRef, getRefValue, setActiveSlide, setOffsetX, gap]
  );

  const onClickButton = (side: 'right' | 'left') => {
    if (side === 'right' && activeSlide + 1 < numberOfSlides) {
      handlerActiveSlide(activeSlide + 1);
    } else if (side === 'left' && activeSlide > 0) {
      handlerActiveSlide(activeSlide - 1);
    }
  };

  return (
    <Fragment>
      <div className={styles.btn_container}>
        {showButtons && activeSlide > 0 && (
          <button className={styles.carousel_btn_left} onClick={() => onClickButton('left')}>
            <Icon name="chevron_left" size="medium" />
          </button>
        )}
        <div id={id} className={carouselClass} onTouchStart={onTouchStart}>
          <div
            style={{
              transform: `translateX(${offsetX - spaceBetweenSlides * activeSlide}px)`,
              gap: `${gap || 0}px`,
            }}
            ref={containerRef}
            className={carouselContainerClass}
          >
            {Children.map(children, (child: any, key: any) => {
              return (
                <div id={`slide-${key}`} key={`slide-${key}`} className={styles.carousel_element}>
                  {child}
                </div>
              );
            })}
          </div>
        </div>
        {showButtons && activeSlide + 1 < numberOfSlides && (
          <button className={styles.carousel_btn_right} onClick={() => onClickButton('right')}>
            <Icon name="chevron_right" size="large" />
          </button>
        )}
      </div>
      {bulletSlide && (
        <div className={styles.container_bullet}>
          {children &&
            Children.map(children, (_child: any, key: number) => {
              return (
                <p id={`bullet-${key}`} key={`bullet-${key}`} className={bulletClasses(key)} />
              );
            })}
        </div>
      )}
    </Fragment>
  );
}

export default Carousel;
